import { useState } from 'react';
import { db } from '../db/client';
import { signOut } from '../db/auth';
import { LoginPanel } from './LoginPanel';
import { OrgPickerPanel } from './OrgPickerPanel';
import { CommentsTab } from './CommentsTab';
import { MembersTab } from './MembersTab';
import { SettingsTab } from './SettingsTab';
import { avatarColor, avatarInitial, displayName } from './avatar';

type Tab = 'comments' | 'members' | 'settings';

interface SidePanelProps {
  authLoading: boolean;
  authError: string | null;
  user: { id: string; email: string } | null;
  activeOrgId: string | null;
  onPickOrg: (id: string | null) => void;
  onClose: () => void;
}

export function SidePanel({
  authLoading,
  authError,
  user,
  activeOrgId,
  onPickOrg,
  onClose,
}: SidePanelProps) {
  const [tab, setTab] = useState<Tab>('comments');

  return (
    <div className="uin-panel-backdrop" onClick={onClose}>
      <div className="uin-panel" onClick={(e) => e.stopPropagation()}>
        <header className="uin-panel-header">
          <div className="uin-panel-title">
            <span className="uin-logo">●</span>
            <span>UI Noter</span>
            {activeOrgId && user && <ActiveOrgChip orgId={activeOrgId} />}
          </div>
          <button className="uin-icon-btn" onClick={onClose} aria-label="Close">×</button>
        </header>

        {!authLoading && user && (
          <div className="uin-account-row">
            <div
              className="uin-account-avatar"
              style={(() => {
                const c = avatarColor(user.email);
                return { background: c.bg, color: c.fg };
              })()}
            >
              {avatarInitial(user.email)}
            </div>
            <div className="uin-account-meta">
              <div className="uin-account-name">{displayName(user.email)}</div>
              <div className="uin-account-email">{user.email}</div>
            </div>
            <button
              className="uin-btn-secondary uin-account-signout"
              onClick={async () => {
                await signOut();
                onPickOrg(null);
              }}
              title="Sign out"
            >
              Sign out
            </button>
          </div>
        )}

        <div className="uin-panel-body">
          {authLoading && <div className="uin-muted">Loading…</div>}

          {!authLoading && authError && (
            <div className="uin-error">Auth error: {authError}</div>
          )}

          {!authLoading && !user && <LoginPanel />}

          {!authLoading && user && !activeOrgId && (
            <OrgPickerPanel
              userId={user.id}
              userEmail={user.email}
              onPick={onPickOrg}
            />
          )}

          {!authLoading && user && activeOrgId && (
            <>
              <nav className="uin-tabs">
                <button className={tab === 'comments' ? 'active' : ''} onClick={() => setTab('comments')}>Comments</button>
                <button className={tab === 'members' ? 'active' : ''} onClick={() => setTab('members')}>Members</button>
                <button className={tab === 'settings' ? 'active' : ''} onClick={() => setTab('settings')}>Settings</button>
              </nav>

              <div className="uin-tab-body">
                {tab === 'comments' && <CommentsTab orgId={activeOrgId} />}
                {tab === 'members' && <MembersTab orgId={activeOrgId} currentUserId={user.id} />}
                {tab === 'settings' && (
                  <SettingsTab
                    orgId={activeOrgId}
                    currentUserId={user.id}
                    onSwitchOrg={() => onPickOrg(null)}
                  />
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ActiveOrgChip({ orgId }: { orgId: string }) {
  const { data } = db.useQuery({
    organizations: { $: { where: { id: orgId } } },
  });
  const name = data?.organizations?.[0]?.name;
  if (!name) return null;
  return <span className="uin-org-chip">{name}</span>;
}
